import React, { useState, useEffect, useMemo } from 'react';
import {
  Newspaper, Search, Filter, Settings, Bookmark, BookmarkCheck,
  ExternalLink, ChevronDown, Sparkles, X, MapPin, Globe, Building2,
  BookOpen, Rss, Clock, ThumbsUp, ThumbsDown, Check, AlertTriangle,
  Tag, FileText, StickyNote, Loader2, RefreshCcw, User, EyeOff, Eye, Shield
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGetJson as apiGet, apiPostJson as apiPost, apiPutJson as apiPut, apiDeleteJson as apiDelete, apiPatchJson as apiPatch } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';

const IDENTITY_PRESETS = [
  { value: 'syndicator',                label: 'Syndicator' },
  { value: 'developer',                 label: 'Developer' },
  { value: 'general_contractor',        label: 'General Contractor / Builder' },
  { value: 'architect',                 label: 'Architect / Designer' },
  { value: 'asset_manager',             label: 'Asset Manager' },
  { value: 'property_manager',          label: 'Property Manager' },
  { value: 'compliance_professional',   label: 'Compliance Professional' },
  { value: 'hfa_staff',                 label: 'State HFA Staff' },
  { value: 'pha_staff',                 label: 'Public Housing Authority Staff' },
  { value: 'hud_contractor',            label: 'HUD Contractor / Monitor' },
  { value: 'tax_credit_accountant',     label: 'Tax Credit Accountant / CPA' },
  { value: 'housing_attorney',          label: 'Housing Attorney' },
  { value: 'investor',                  label: 'Investor' },
  { value: 'lender',                    label: 'Lender' },
  { value: 'loan_officer',              label: 'Loan Officer / Underwriter' },
  { value: 'appraiser',                 label: 'Appraiser' },
  { value: 'real_estate_broker',        label: 'Real Estate Broker' },
  { value: 'consultant',                label: 'LIHTC / Housing Consultant' },
  { value: 'nonprofit_executive',       label: 'Non-profit Executive' },
  { value: 'policy_researcher',         label: 'Housing Policy Researcher / Academic' },
  { value: 'resident_services',         label: 'Resident Services Coordinator' },
  { value: 'resident',                  label: 'Resident / Tenant' },
  { value: 'homeowner_hoa',             label: 'Homeowner / HOA' }
];
const identityDisplay = (v) => {
  if (!v) return null;
  return IDENTITY_PRESETS.find(o => o.value === v)?.label || v;
};

// --- Constants ---
const CATEGORY_META = {
  federal:      { label: 'Federal',      icon: Building2, color: 'from-blue-500/10 to-indigo-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40' },
  association:  { label: 'Association',  icon: Globe,     color: 'from-purple-500/10 to-pink-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/40' },
  state_hfa:    { label: 'State HFA',    icon: MapPin,    color: 'from-green-500/10 to-emerald-500/10 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/40' },
  industry_pub: { label: 'Industry Pub', icon: BookOpen,  color: 'from-orange-500/10 to-amber-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800/40' },
  other:        { label: 'Other',        icon: Rss,       color: 'from-gray-500/10 to-slate-500/10 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700' }
};

const TABS = [
  { key: 'all',          label: 'All' },
  { key: 'saved',        label: 'Saved' },
  { key: 'federal',      label: 'Federal' },
  { key: 'state_hfa',    label: 'State HFAs' },
  { key: 'association',  label: 'Associations' },
  { key: 'industry_pub', label: 'Industry' }
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

// --- Page ---
const NewsFeedPage = () => {
  const qc = useQueryClient();
  const { currentUser, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [openArticle, setOpenArticle] = useState(null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [saveMenuFor, setSaveMenuFor] = useState(null);

  // Close modal on Escape
  useEffect(() => {
    if (!openArticle) return;
    const handler = (e) => { if (e.key === 'Escape') setOpenArticle(null); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [openArticle]);
  const [identityBannerDismissed, setIdentityBannerDismissed] = useState(
    () => localStorage.getItem('jpro_news_identity_banner_dismissed') === '1'
  );
  const professionalIdentity = currentUser?.professionalIdentity || currentUser?.professional_identity || '';
  const showIdentityBanner = !professionalIdentity && !identityBannerDismissed;

  const dismissIdentityBanner = () => {
    localStorage.setItem('jpro_news_identity_banner_dismissed', '1');
    setIdentityBannerDismissed(true);
  };

  // Mark viewed on mount so unread count resets
  useEffect(() => { apiPost('/news/mark_viewed', {}).catch(() => {}); }, []);

  // Admin-only toggle: include hidden articles in the feed so admins
  // can find and unhide them. Persisted across reloads.
  const isAdmin = !!currentUser?.admin;
  const [showHidden, setShowHidden] = useState(() => isAdmin && localStorage.getItem('jpro_news_show_hidden') === '1');
  useEffect(() => { localStorage.setItem('jpro_news_show_hidden', showHidden ? '1' : '0'); }, [showHidden]);

  const listQueryKey = useMemo(
    () => ['news-articles', activeTab, search, stateFilter, showHidden],
    [activeTab, search, stateFilter, showHidden]
  );

  const { data: listData, isLoading: loadingList, error: listError } = useQuery({
    queryKey: listQueryKey,
    queryFn: async () => {
      if (activeTab === 'saved') {
        return apiGet('/news/saved');
      }
      const qs = new URLSearchParams();
      qs.set('limit', '30');
      if (search)    qs.set('search', search);
      if (stateFilter) qs.set('state', stateFilter);
      if (['federal','state_hfa','association','industry_pub','other'].includes(activeTab)) {
        qs.set('category', activeTab);
      }
      if (isAdmin && showHidden) qs.set('include_hidden', '1');
      return apiGet(`/news/articles?${qs.toString()}`);
    },
    keepPreviousData: true
  });

  const { data: prefs } = useQuery({
    queryKey: ['news-preferences'],
    queryFn: () => apiGet('/news/preferences')
  });

  const { data: briefData } = useQuery({
    queryKey: ['news', 'brief'],
    queryFn: () => apiGet('/news/brief'),
    staleTime: 1000 * 60 * 30,
  });
  const brief = briefData?.brief || null;

  const articles = listData?.articles || [];

  const savePrefsMut = useMutation({
    mutationFn: (preferences) => apiPut('/news/preferences', { preferences }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-preferences'] });
      qc.invalidateQueries({ queryKey: ['news-articles'] });
    }
  });

  const hideArticleMut = useMutation({
    mutationFn: ({ id, admin_hidden }) => apiPatch(`/admin/news/articles/${id}`, { admin_hidden }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-articles'] });
    }
  });

  // Article pending hide confirmation. Unhide goes straight through —
  // it's reversible/non-destructive so no prompt needed.
  const [confirmHideArticle, setConfirmHideArticle] = useState(null);

  const requestHide = (article) => setConfirmHideArticle(article);
  const cancelHide  = () => setConfirmHideArticle(null);
  const confirmHide = () => {
    if (!confirmHideArticle) return;
    hideArticleMut.mutate({ id: confirmHideArticle.id, admin_hidden: true });
    setConfirmHideArticle(null);
  };

  return (
    <div className="h-full flex flex-col max-w-5xl mx-auto px-4 sm:px-6 pt-6">
      {/* Header — editorial masthead */}
      <div className="shrink-0 flex items-end justify-between gap-4 flex-wrap mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 dark:text-gray-500 mb-1">
            Affordable housing &amp; LIHTC
          </div>
          <h1 className="text-2xl font-rasa font-semibold text-gray-900 dark:text-white tracking-tight">News</h1>
          <p className="text-sm font-rasa text-gray-500 dark:text-gray-400 mt-0.5">
            Regulatory updates tailored to your portfolio. Click any story for JPRO's take.
          </p>
        </div>
        <button
          onClick={() => setPrefsOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Settings size={14} /> Preferences
        </button>
      </div>

      {/* Inline identity onboarding (shown only when not set + not dismissed + tailoring isn't disabled) */}
      {showIdentityBanner && prefs?.preferences?.tailoring_enabled !== false && (
        <IdentityOnboardBanner
          onSave={async (v) => { await updateProfile({ professionalIdentity: v }); dismissIdentityBanner(); }}
          onDeclineTailoring={async () => {
            await savePrefsMut.mutateAsync({
              selected_state_codes: prefs?.preferences?.selected_state_codes || [],
              muted_source_ids:     prefs?.preferences?.muted_source_ids || [],
              muted_tags:           prefs?.preferences?.muted_tags || [],
              tailoring_enabled:    false
            });
            dismissIdentityBanner();
          }}
          onSkip={dismissIdentityBanner}
        />
      )}

      {/* Tab bar */}
      <div className="shrink-0 flex items-center gap-1 mb-5 overflow-x-auto -mx-1 px-1">
        {TABS.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3.5 py-1.5 text-xs font-medium whitespace-nowrap rounded-full border transition-all ${
                active
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filter bar — single pill with search and state filter */}
      {activeTab !== 'saved' && (
        <div className="shrink-0 flex items-center gap-2 mb-5 flex-wrap">
          <div className="flex items-center flex-1 min-w-[200px] rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus-within:border-gray-400 dark:focus-within:border-gray-500 transition-colors">
            <Search size={14} className="ml-3.5 text-gray-400 shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stories"
              className="flex-1 px-2.5 py-2 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
            />
            <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="pr-3.5 py-2 bg-transparent text-sm text-gray-700 dark:text-gray-200 focus:outline-none cursor-pointer"
            >
              <option value="">All states</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {(search || stateFilter) && (
            <button
              onClick={() => { setSearch(''); setStateFilter(''); }}
              className="text-xs font-medium text-gray-500 hover:text-gray-900 dark:hover:text-white underline underline-offset-2"
            >
              Clear
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowHidden((v) => !v)}
              title="Admin: include articles hidden by admins"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                showHidden
                  ? 'bg-orange-50 border-orange-300 text-orange-800 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-200'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
            >
              <Shield size={11} /> {showHidden ? 'Showing hidden' : 'Show hidden'}
            </button>
          )}
        </div>
      )}

      {/* Scrollable article list region */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 pb-6">
        {loadingList && (
          <div className="space-y-3">
            {[1,2,3].map((i) => (
              <div key={i} className="animate-pulse h-28 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" />
            ))}
          </div>
        )}

        {listError && (
          <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800/40 p-4 text-sm text-red-800 dark:text-red-300">
            Failed to load articles: {listError.message}
          </div>
        )}

        {!loadingList && articles.length === 0 && (
          <div className="text-center py-14">
            <Newspaper size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {activeTab === 'saved' ? 'Nothing saved yet' : 'No articles match your filters'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {activeTab === 'saved' ? 'Save articles from the main feed to keep them here.' : 'Try clearing filters or widening your state selection in Preferences.'}
            </p>
          </div>
        )}

        {brief && activeTab !== 'saved' && <JproBriefHero brief={brief} />}

        <div className="space-y-1.5">
          {articles.map((a) => (
            <ArticleCard
              key={a.id}
              article={a}
              isAdmin={isAdmin}
              onOpen={() => setOpenArticle(a)}
              onOpenSaveMenu={() => setSaveMenuFor(saveMenuFor === a.id ? null : a.id)}
              saveMenuOpen={saveMenuFor === a.id}
              closeSaveMenu={() => setSaveMenuFor(null)}
              onToggleHidden={(hidden) => {
                if (hidden) requestHide(a);
                else hideArticleMut.mutate({ id: a.id, admin_hidden: false });
              }}
              hideBusy={hideArticleMut.isPending && hideArticleMut.variables?.id === a.id}
            />
          ))}
        </div>
      </div>

      {/* Article modal */}
      {openArticle && (
        <ArticleModal
          article={openArticle}
          tailoringEnabled={prefs?.preferences?.tailoring_enabled !== false}
          onClose={() => setOpenArticle(null)}
        />
      )}

      {/* Hide-article confirmation */}
      {confirmHideArticle && (
        <ConfirmHideModal
          article={confirmHideArticle}
          busy={hideArticleMut.isPending}
          onCancel={cancelHide}
          onConfirm={confirmHide}
        />
      )}

      {/* Preferences modal */}
      {prefsOpen && prefs && (
        <PreferencesModal
          prefs={prefs.preferences}
          availableStates={prefs.available_states || []}
          availableSources={prefs.available_sources || []}
          currentIdentity={professionalIdentity}
          onSaveIdentity={(v) => updateProfile({ professionalIdentity: v })}
          onSave={(p) => { savePrefsMut.mutate(p); setPrefsOpen(false); }}
          onClose={() => setPrefsOpen(false)}
          saving={savePrefsMut.isPending}
        />
      )}
    </div>
  );
};

// --- Inline identity onboard banner ---
const IdentityOnboardBanner = ({ onSave, onDeclineTailoring, onSkip }) => {
  const [choice, setChoice] = useState('');
  const [custom, setCustom] = useState('');
  const [mode, setMode] = useState('preset'); // 'preset' | 'custom'
  const [saving, setSaving] = useState(false);
  const [declining, setDeclining] = useState(false);

  const pickValue = mode === 'custom' ? custom.trim() : choice;

  const handleSave = async () => {
    if (!pickValue) return;
    setSaving(true);
    try { await onSave(pickValue); } finally { setSaving(false); }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try { await onDeclineTailoring(); } finally { setDeclining(false); }
  };

  return (
    <div className="mb-5 rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-900/20 dark:to-cyan-900/20 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="shrink-0 p-1.5 rounded-md bg-gray-100 dark:bg-gray-700">
          <Sparkles size={14} className="text-indigo-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold font-rasa text-gray-900 dark:text-white">
            Tailor news to your work?
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Tell JPRO your role and every article gets a "why it matters to you" framed for it.
            Skip to keep the feed general — you can change your mind any time.
          </p>

          <div className="mt-3 flex gap-2 flex-wrap items-center">
            {mode === 'preset' ? (
              <>
                <select
                  value={choice}
                  onChange={(e) => setChoice(e.target.value)}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                >
                  <option value="">— Pick a role —</option>
                  {IDENTITY_PRESETS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setMode('custom'); setChoice(''); }}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline"
                >
                  Or type your own
                </button>
              </>
            ) : (
              <>
                <input
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Housing policy researcher"
                  className="flex-1 min-w-[200px] px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => { setMode('preset'); setCustom(''); }}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline"
                >
                  Back to presets
                </button>
              </>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={!pickValue || saving}
              className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg shadow-sm"
            >
              {saving ? 'Saving…' : 'Save role'}
            </button>
          </div>

          <div className="mt-3 flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={handleDecline}
              disabled={declining}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline disabled:opacity-50"
            >
              {declining ? 'Saving…' : 'No thanks — show me general news'}
            </button>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <button
              type="button"
              onClick={onSkip}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline"
            >
              Ask me later
            </button>
          </div>
        </div>
        <button
          onClick={onSkip}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 -mt-1 -mr-1"
          title="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// --- Article card ---
// --- JPRO Brief hero: shared daily 48h synthesis, dismissible per day ---
const JproBriefHero = ({ brief }) => {
  const dayKey = `jpro_brief_dismissed_${brief.brief_date}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dayKey) === '1');

  // Resync when a new day's brief arrives while mounted (React Query refresh),
  // so a prior day's dismissal doesn't keep hiding the new brief.
  useEffect(() => {
    setDismissed(localStorage.getItem(dayKey) === '1');
  }, [dayKey]);

  if (dismissed || !brief.items?.length) return null;

  const dismiss = () => {
    localStorage.setItem(dayKey, '1');
    setDismissed(true);
  };

  const dateLabel = brief.brief_date
    ? new Date(`${brief.brief_date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="mb-3 rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50/60 dark:bg-blue-900/15 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-block rounded px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide bg-blue-600 text-white">
              JPRO Brief
            </span>
            {dateLabel && <span className="text-[11px] text-gray-500 dark:text-gray-400">{dateLabel}</span>}
          </div>
          {brief.headline && (
            <h3 className="mt-1.5 text-[15px] font-semibold font-rasa text-gray-900 dark:text-white leading-snug">
              {brief.headline}
            </h3>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 -mr-1 -mt-0.5 p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-blue-100 dark:hover:bg-blue-900/30"
          title="Dismiss for today"
          aria-label="Dismiss the JPRO Brief for today"
        >
          <X size={15} />
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {brief.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-gray-700 dark:text-gray-300 leading-relaxed">
            <span className="mt-[7px] shrink-0 h-1.5 w-1.5 rounded-full bg-blue-500" />
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ArticleCard = ({ article, isAdmin = false, onOpen, onOpenSaveMenu, saveMenuOpen, closeSaveMenu, onToggleHidden, hideBusy = false }) => {
  const cat = CATEGORY_META[article.source?.category] || CATEGORY_META.other;
  const Icon = cat.icon;
  const isHidden = !!article.admin_hidden_at;

  // Use a clickable div (not a <button>) so we can nest the save button inside
  // without breaking HTML validity.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className={`group relative rounded-lg border hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900 ${
        isHidden
          ? 'bg-orange-50/50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/40 hover:bg-orange-50 dark:hover:bg-orange-900/20'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-800/80'
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {/* Byline — tight, single line */}
            <div className="flex items-center gap-1.5 text-[10.5px] text-gray-500 dark:text-gray-400 mb-1">
              <span className="font-semibold text-gray-700 dark:text-gray-200 truncate max-w-[180px]">
                {article.source?.organization || cat.label}
              </span>
              {article.source?.state_code && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span>{article.source.state_code}</span>
                </>
              )}
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>{formatDate(article.published_at)}</span>
            </div>

            {/* Title — tighter, no giant sizes */}
            <h3 className="text-[15px] font-semibold font-rasa text-gray-900 dark:text-white leading-[1.35] group-hover:text-gray-950 dark:group-hover:text-white">
              {article.title}
            </h3>

            {/* Summary — single line, muted */}
            {article.summary && (
              <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400 line-clamp-1 leading-relaxed">
                {stripHtml(article.summary)}
              </p>
            )}

            {/* JPRO Take — shared one-line hook to tempt the click */}
            {article.jpro_take && (
              <p className="mt-1.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-blue-700 dark:text-blue-300">
                <span className="shrink-0 mt-[1px] inline-block rounded px-1 py-[1px] text-[9px] font-bold uppercase tracking-wide bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  JPRO Take
                </span>
                <span className="font-medium">{article.jpro_take}</span>
              </p>
            )}
          </div>

          {/* Right-side controls: Save (everyone), Hide/Unhide (admins only) */}
          <div className="shrink-0 flex items-center gap-0.5 relative -mr-1" onClick={(e) => e.stopPropagation()}>
            {isAdmin && (
              <button
                type="button"
                onClick={() => onToggleHidden?.(!isHidden)}
                disabled={hideBusy}
                className={`p-1.5 rounded-md transition-colors disabled:opacity-50 ${
                  isHidden
                    ? 'text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                    : 'text-gray-300 dark:text-gray-500 hover:text-orange-700 dark:hover:text-orange-300'
                }`}
                title={isHidden ? 'Hidden by admin — click to unhide' : 'Hide from all users'}
                aria-label={isHidden ? 'Unhide article' : 'Hide article'}
              >
                {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            )}
            <button
              type="button"
              onClick={onOpenSaveMenu}
              className={`p-1.5 rounded-md transition-colors ${
                article.saved
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-300 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
              title={article.saved ? 'Saved' : 'Save'}
            >
              {article.saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
            </button>
            {saveMenuOpen && <SaveMenu article={article} onClose={closeSaveMenu} />}
          </div>
        </div>
        {isHidden && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-medium text-orange-800 dark:text-orange-300">
            <Shield size={10} /> Hidden from all users
          </div>
        )}
      </div>
    </div>
  );
};

// --- Article modal (opens on card click) ---
const ArticleModal = ({ article, tailoringEnabled, onClose }) => {
  const qc = useQueryClient();
  const [explicitlyRequested, setExplicitlyRequested] = useState(false);

  // Full article detail (summary + any cached tailoring)
  const { data: detail, isLoading: loadingDetail } = useQuery({
    queryKey: ['news-article', article.id],
    queryFn: () => apiGet(`/news/articles/${article.id}`)
  });
  const cachedTailoring = detail?.tailoring;

  // Auto-fetch tailoring when the user has opted in and no cached tailoring exists.
  // Gated by `enabled` — no useEffect needed.
  const autoShouldFetch = tailoringEnabled || explicitlyRequested;
  const tailorQuery = useQuery({
    queryKey: ['news-tailoring', article.id],
    queryFn: async () => {
      const res = await apiPost(`/news/articles/${article.id}/tailor`, {});
      return res.tailoring;
    },
    enabled: !!detail && !cachedTailoring && autoShouldFetch,
    retry: false,
    staleTime: Infinity
  });

  const tailoring = cachedTailoring || tailorQuery.data;
  const tailoringPending = tailorQuery.isFetching && !tailoring;
  const tailoringError   = tailorQuery.error;

  // Feedback mutation
  const feedbackMut = useMutation({
    mutationFn: (helpful) => apiPost(`/news/articles/${article.id}/tailor/feedback`, { helpful }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['news-article', article.id] })
  });

  const cat = CATEGORY_META[article.source?.category] || CATEGORY_META.other;
  const Icon = cat.icon;
  const badgeColor = {
    federal:      'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    association:  'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    state_hfa:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    industry_pub: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    other:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }[article.source?.category] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white dark:bg-gray-900 w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 my-0 sm:my-8 max-h-screen sm:max-h-[calc(100vh-4rem)] flex flex-col"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/90 dark:bg-gray-900/90 backdrop-blur text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-gray-800 z-20 shadow-sm border border-gray-200 dark:border-gray-700"
          title="Close"
        >
          <X size={16} />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto">
          {/* Hero image */}
          {article.image_url && (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-52 sm:h-64 object-cover sm:rounded-t-2xl"
            />
          )}

          {/* Article body */}
          <article className="px-6 sm:px-10 pt-8 pb-6">
            {/* Source pill */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${badgeColor}`}>
                <Icon size={11} /> {article.source?.organization || cat.label}
              </span>
              {article.source?.state_code && (
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                  <MapPin size={10} /> {article.source.state_code}
                </span>
              )}
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">{formatDate(article.published_at)}</span>
            </div>

            {/* Title — the reading anchor */}
            <h2 className="font-rasa font-semibold text-gray-900 dark:text-white text-2xl sm:text-3xl leading-[1.2] tracking-tight mb-3">
              {article.title}
            </h2>

            {/* Tags under title */}
            {(article.detected_programs || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-6">
                {article.detected_programs.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60 px-1.5 py-0.5 rounded">
                    <Tag size={9} /> {p}
                  </span>
                ))}
              </div>
            )}

            {/* Subtle divider */}
            <div className="w-16 h-px bg-gray-300 dark:bg-gray-600 mb-6" />

            {/* RSS summary body */}
            {loadingDetail ? (
              <div className="animate-pulse space-y-2.5">
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-11/12" />
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-10/12" />
                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-9/12" />
              </div>
            ) : (
              (detail?.article?.summary || detail?.article?.content) && (
                <div
                  className="font-rasa text-[15px] text-gray-800 dark:text-gray-200 leading-[1.7] [&_a]:text-indigo-600 [&_a]:underline [&_a:hover]:text-indigo-800 dark:[&_a]:text-indigo-400 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3"
                  dangerouslySetInnerHTML={{ __html: detail.article.content || detail.article.summary }}
                />
              )
            )}

            {/* JPRO tailoring panel */}
            <div className="mt-6 rounded-xl bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-900/20 dark:to-cyan-900/20 border border-emerald-200/60 dark:border-emerald-800/40 p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-md bg-white/80 dark:bg-gray-900/60">
                  <Sparkles size={13} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-900 dark:text-emerald-200">JPRO's take for you</span>
                {tailoring?.confidence && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${
                    tailoring.confidence === 'high' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' :
                    tailoring.confidence === 'low'  ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' :
                                                      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                  }`}>{tailoring.confidence} confidence</span>
                )}
              </div>

              {/* Opt-out state with no tailoring yet */}
              {!autoShouldFetch && !tailoring && (
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Tailoring is off for this feed.
                  <button
                    onClick={() => setExplicitlyRequested(true)}
                    className="ml-1 text-emerald-700 dark:text-emerald-300 font-medium hover:underline"
                  >
                    Ask JPRO to tailor this one →
                  </button>
                </div>
              )}

              {tailoringPending && (
                <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200 py-4">
                  <Loader2 size={14} className="animate-spin" />
                  JPRO is reading this for you…
                </div>
              )}

              {tailoringError && !tailoring && (
                <div className="rounded-lg border border-orange-200 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/30 p-3 text-sm text-orange-900 dark:text-orange-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <div>
                      JPRO tailoring is temporarily unavailable.
                      <button onClick={() => tailorQuery.refetch()} className="ml-2 underline hover:no-underline">Retry</button>
                    </div>
                  </div>
                </div>
              )}

              {tailoring && (
                <div className="space-y-4">
                  <TailoringField label="Synopsis" body={tailoring.synopsis} />
                  <TailoringField label="Why it matters to you" body={tailoring.why_it_matters} />
                  <TailoringField label="Potential impact" body={tailoring.potential_impact} />
                  <TailoringField label="Recommended action" body={tailoring.recommended_action} preserveWhitespace />

                  {/* Sources JPRO checked — hidden for now
                  {tailoring.grounding_sources?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:text-emerald-200 mb-1.5">Sources JPRO checked</div>
                      <div className="flex flex-wrap gap-2">
                        {tailoring.grounding_sources.map((s, i) => (
                          <a
                            key={i}
                            href={s.url}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300 bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-700 px-2 py-1 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                          >
                            <ExternalLink size={10} /> {s.title || safeHost(s.url)}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  */}

                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-200/60 dark:border-emerald-800/40">
                    <span className="text-[11px] text-emerald-900 dark:text-emerald-200">Was this helpful?</span>
                    <button
                      onClick={() => feedbackMut.mutate(true)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                        tailoring.user_feedback === 'helpful'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 ring-1 ring-green-400'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                    ><ThumbsUp size={11} /> Helpful</button>
                    <button
                      onClick={() => feedbackMut.mutate(false)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                        tailoring.user_feedback === 'not_helpful'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 ring-1 ring-red-400'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                    ><ThumbsDown size={11} /> Not helpful</button>
                    <button
                      onClick={() => tailorQuery.refetch()}
                      disabled={tailorQuery.isFetching}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-emerald-700 dark:text-emerald-300 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
                    ><RefreshCcw size={11} className={tailorQuery.isFetching ? 'animate-spin' : ''} /> Re-tailor</button>
                  </div>
                </div>
              )}

              {tailoring?.disclaimer && (
                <p className="mt-4 font-rasa text-[11px] text-emerald-800/80 dark:text-emerald-300/80 italic">
                  {tailoring.disclaimer}
                </p>
              )}
            </div>
          </article>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
          <a
            href={article.url}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            Read original <ExternalLink size={13} />
          </a>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

function safeHost(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

const TailoringField = ({ label, body, preserveWhitespace }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:text-emerald-200 mb-1">{label}</div>
    <p className={`font-rasa text-sm text-gray-800 dark:text-gray-100 leading-relaxed ${preserveWhitespace ? 'whitespace-pre-line' : ''}`}>
      {body || <span className="italic text-gray-400">—</span>}
    </p>
  </div>
);

// --- Save menu ---
const SaveMenu = ({ article, onClose }) => {
  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: (destination) => apiPost(`/news/articles/${article.id}/save`, { destination }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-articles'] });
      qc.invalidateQueries({ queryKey: ['news-saved'] });
      onClose();
    }
  });
  const unsaveMut = useMutation({
    mutationFn: () => apiDelete(`/news/articles/${article.id}/save?destination=saved`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news-articles'] });
      qc.invalidateQueries({ queryKey: ['news-saved'] });
      onClose();
    }
  });

  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('.news-save-menu')) onClose(); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClose]);

  return (
    <div className="news-save-menu absolute right-0 top-10 z-20 w-52 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl py-1">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Save to…</div>
      <button
        onClick={() => saveMut.mutate('saved')}
        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100"
      >
        <Bookmark size={14} /> My Saved Articles
      </button>
      <button
        onClick={() => saveMut.mutate('note')}
        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100"
      >
        <StickyNote size={14} /> myNotes
      </button>
      {article.saved && (
        <>
          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
          <button
            onClick={() => unsaveMut.mutate()}
            className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-700 dark:text-red-300"
          >
            <X size={14} /> Remove from Saved
          </button>
        </>
      )}
    </div>
  );
};

// --- Preferences modal ---
const PreferencesModal = ({ prefs, availableStates, availableSources = [], currentIdentity, onSaveIdentity, onSave, onClose, saving }) => {
  const [selected, setSelected] = useState(new Set(prefs?.selected_state_codes || []));
  const [tailoringEnabled, setTailoringEnabled] = useState(prefs?.tailoring_enabled !== false);

  // Source selection: track which source IDs the user wants ON.
  // Persisted as the inverse — `muted_source_ids` = available - selected.
  const initialActiveSourceIds = useMemo(() => {
    const muted = new Set((prefs?.muted_source_ids || []).map(Number));
    return new Set(availableSources.filter((s) => !muted.has(s.id)).map((s) => s.id));
  }, [prefs, availableSources]);
  const [activeSourceIds, setActiveSourceIds] = useState(initialActiveSourceIds);

  // Identity sub-form
  const initialIsCustom = !!currentIdentity && !IDENTITY_PRESETS.some(o => o.value === currentIdentity);
  const [identity, setIdentity] = useState(currentIdentity || '');
  const [identityMode, setIdentityMode] = useState(initialIsCustom ? 'custom' : 'preset');
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identitySaved, setIdentitySaved] = useState(false);

  // Group sources by category for the UI
  const sourcesByCategory = useMemo(() => {
    const groups = {};
    for (const s of availableSources) {
      const key = s.category || 'other';
      (groups[key] ||= []).push(s);
    }
    return groups;
  }, [availableSources]);

  const toggleState = (s) => {
    const next = new Set(selected);
    next.has(s) ? next.delete(s) : next.add(s);
    setSelected(next);
  };

  const toggleSource = (id) => {
    const next = new Set(activeSourceIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setActiveSourceIds(next);
  };

  const setCategoryAll = (categoryKey, on) => {
    const next = new Set(activeSourceIds);
    for (const s of (sourcesByCategory[categoryKey] || [])) {
      on ? next.add(s.id) : next.delete(s.id);
    }
    setActiveSourceIds(next);
  };

  const save = () => {
    const availableIds = availableSources.map((s) => s.id);
    const mutedFromAvailable = availableIds.filter((id) => !activeSourceIds.has(id));
    // Preserve any pre-existing muted IDs we don't recognize (e.g.,
    // sources outside the user's visibility scope) so we don't unmute
    // them by accident.
    const known = new Set(availableIds);
    const preservedMuted = (prefs?.muted_source_ids || []).map(Number).filter((id) => !known.has(id));

    onSave({
      selected_state_codes: Array.from(selected),
      muted_source_ids:  Array.from(new Set([...preservedMuted, ...mutedFromAvailable])),
      muted_tags:        prefs?.muted_tags || [],
      tailoring_enabled: tailoringEnabled
    });
  };

  const saveIdentity = async () => {
    if (!identity.trim() || identity.trim() === currentIdentity) return;
    setIdentitySaving(true);
    try {
      await onSaveIdentity(identity.trim());
      setIdentitySaved(true);
      setTimeout(() => setIdentitySaved(false), 2000);
    } finally {
      setIdentitySaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold font-rasa text-gray-900 dark:text-white">News preferences</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Control how JPRO tailors news for you.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Tailoring toggle */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setTailoringEnabled((v) => !v)}
              className={`mt-0.5 shrink-0 w-9 h-5 rounded-full transition-colors ${tailoringEnabled ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              role="switch"
              aria-checked={tailoringEnabled}
            >
              <span className={`block w-4 h-4 bg-white rounded-full shadow transform transition-transform ${tailoringEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                <Sparkles size={13} className="text-indigo-500" /> Tailor news to my role
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When on, expanding an article calls JPRO for a personalized "why it matters to you."
                Turn off to read only the original article summaries — fully general news.
              </p>
            </div>
          </div>

          {/* Professional identity (hidden when tailoring is off) */}
          {tailoringEnabled && (
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 mb-2">
              <User size={11} /> Your professional identity
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Drives the tone and framing of JPRO's take on each article.
            </p>
            <div className="flex gap-2 flex-wrap items-center">
              {identityMode === 'preset' ? (
                <select
                  value={identity}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setIdentityMode('custom');
                      setIdentity('');
                    } else {
                      setIdentity(e.target.value);
                    }
                  }}
                  className="flex-1 min-w-[200px] px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                >
                  <option value="">— Not set —</option>
                  {IDENTITY_PRESETS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                  <option value="__custom__">Other / custom…</option>
                </select>
              ) : (
                <>
                  <input
                    value={identity}
                    onChange={(e) => setIdentity(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Housing policy researcher"
                    className="flex-1 min-w-[200px] px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                  />
                  <button
                    type="button"
                    onClick={() => { setIdentityMode('preset'); setIdentity(currentIdentity && IDENTITY_PRESETS.some(o => o.value === currentIdentity) ? currentIdentity : ''); }}
                    className="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  >
                    Use preset
                  </button>
                </>
              )}
              <button
                onClick={saveIdentity}
                disabled={identitySaving || !identity.trim() || identity.trim() === currentIdentity}
                className="inline-flex items-center gap-1 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg disabled:opacity-40"
              >
                {identitySaving ? '…' : identitySaved ? <><Check size={12}/> Saved</> : 'Update role'}
              </button>
            </div>
          </div>
          )}

          {/* Sources */}
          {availableSources.length > 0 && (
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 mb-2">
                <Newspaper size={11} /> Sources to follow
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Toggle whole categories or pick specific outlets. Unchecked sources are hidden from your feed.
              </p>

              <div className="space-y-3">
                {Object.keys(sourcesByCategory).map((catKey) => {
                  const meta = CATEGORY_META[catKey] || CATEGORY_META.other;
                  const Icon = meta.icon;
                  const items = sourcesByCategory[catKey] || [];
                  if (items.length === 0) return null;
                  const onCount = items.filter((s) => activeSourceIds.has(s.id)).length;
                  const allOn   = onCount === items.length;
                  const noneOn  = onCount === 0;

                  return (
                    <div key={catKey} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2">
                          <Icon size={13} className="text-gray-500 dark:text-gray-400" />
                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{meta.label}</span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {onCount}/{items.length}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setCategoryAll(catKey, true)}
                            disabled={allOn}
                            className={`px-2 py-0.5 text-[11px] rounded ${
                              allOn
                                ? 'text-gray-400 dark:text-gray-600 cursor-default'
                                : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                            }`}
                          >
                            All
                          </button>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <button
                            type="button"
                            onClick={() => setCategoryAll(catKey, false)}
                            disabled={noneOn}
                            className={`px-2 py-0.5 text-[11px] rounded ${
                              noneOn
                                ? 'text-gray-400 dark:text-gray-600 cursor-default'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                          >
                            None
                          </button>
                        </div>
                      </div>
                      <div className="p-2 flex flex-wrap gap-1.5">
                        {items.map((s) => {
                          const isOn = activeSourceIds.has(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => toggleSource(s.id)}
                              title={s.organization ? `${s.organization} — ${s.name}` : s.name}
                              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                                isOn
                                  ? 'bg-indigo-50 border-indigo-400 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-500 dark:text-indigo-200'
                                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 line-through'
                              }`}
                            >
                              {s.organization || s.name}
                              {s.scope === 'state' && s.state_code ? (
                                <span className="ml-1 opacity-60">· {s.state_code}</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* States */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 mb-2">
              <MapPin size={11} /> States to follow
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Pick any state — a small green dot means there's an active source for it today.
              Others stay a preference until an admin adds a source for that state.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {availableStates.map((s) => {
                const code    = typeof s === 'string' ? s : s.code;
                const hasSrc  = typeof s === 'string' ? true : !!s.has_sources;
                const isOn    = selected.has(code);
                return (
                  <button
                    key={code}
                    onClick={() => toggleState(code)}
                    className={`relative px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      isOn
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                        : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                    }`}
                    title={hasSrc ? 'Has an active news source' : 'No source yet — selection will apply when one is added'}
                  >
                    {code}
                    {hasSrc && (
                      <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ring-2 ${
                        isOn ? 'bg-green-300 ring-indigo-600' : 'bg-green-500 ring-white dark:ring-gray-800'
                      }`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 bg-gray-50 dark:bg-gray-900/40 rounded-b-2xl">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md shadow-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Hide confirmation ---
const ConfirmHideModal = ({ article, busy, onCancel, onConfirm }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && !busy) onConfirm();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [busy, onCancel, onConfirm]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md"
      >
        <div className="p-5 flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <AlertTriangle size={18} className="text-orange-600 dark:text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white">Hide this article?</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              It'll be removed from every user's feed. You can unhide it later from the
              admin "Show hidden" view.
            </p>
            <div className="mt-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {article.source?.organization || 'Article'}
              </div>
              <div className="text-sm text-gray-900 dark:text-white line-clamp-2">
                {article.title}
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 bg-gray-50 dark:bg-gray-900/40 rounded-b-2xl">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-md shadow-sm disabled:opacity-50"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Hiding…</> : <><EyeOff size={13} /> Hide article</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Helpers ---
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}
function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 86_400_000) return 'Today';
  if (diff < 2 * 86_400_000) return 'Yesterday';
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: diff > 365 * 86_400_000 ? 'numeric' : undefined });
}

export default NewsFeedPage;
